import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { all_routes } from "../router/all_routes";
import {
  deleteClient,
  fetchClients,
  FetchedClient,
  getClientWidgets,
  updateClient,
} from "../../core/api/useCompanies";
import Table from "../../core/common/dataTable/index";
import { createClient } from "../../core/api/useCompanies";
import { toast } from "react-toastify";
import { fetchProducts } from "../../core/api/products";
import { fetchProductList, ProductType } from "../../core/api/useCompanies";
import Select from "react-select";
import { Client } from "../../core/api/useCompanies";
import { Dropdown, InputGroup, FormControl } from "react-bootstrap";
import { Button, Input, Modal, Tag } from "antd";
import ClientExportImport from "./companiesImport";
import { useAuthToken } from "../../core/data/redux/useAuthToken";
import { usePermission } from "../../core/api/useRolePermission";

type OptionType = {
  value: string;
  label: string;
};

const TmsCompanies: React.FC = () => {
  const empToken = useAuthToken();
  const routes = all_routes;
  const [companies, setCompanies] = useState<FetchedClient[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<FetchedClient[]>(
    []
  );
  const [searchText, setSearchText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const capitalizeFirstLetter = (str: string) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  // Permissions
  const canCreate = usePermission("Companies", "create");
  const canUpdate = usePermission("Companies", "write");
  const canDelete = usePermission("Companies", "delete");
  const canRead = usePermission("Companies", "read");
  const canExport = usePermission("Companies", "export");

  const handleDelete = (clientId: string) => {
    if (!canDelete) {
      toast.warning("You don't have permission to delete companies.");
      return;
    }
    setSelectedClientId(clientId);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedClientId) return;
    try {
      await deleteClient(selectedClientId, empToken);
      await refreshData();
    } catch (error) {
      console.error(error);
    } finally {
      setShowConfirm(false);
      setSelectedClientId(null);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    const lowercased = value.toLowerCase();
    const filtered = companies.filter((company) =>
      Object.values(company).join(" ").toLowerCase().includes(lowercased)
    );
    setFilteredCompanies(filtered);
  };

  useEffect(() => {
    if (!searchText) {
      setFilteredCompanies([]);
      return;
    }

    const lowercased = searchText.toLowerCase();

    const filtered = companies.filter((company) => {
      const {
        company_name,
        company_license,
        email,
        server_name,
        status,
        contact_number,
        company_batch,
        product,
        modules,
      } = company;

      const moduleNames = (modules || [])
        .map((mod: any) => (typeof mod === "object" ? mod.name : mod))
        .join(",");

      const valuesToSearch = [
        company_name,
        company_license,
        email,
        server_name,
        status,
        contact_number?.toString(),
        company_batch,
        product?.product_name,
        moduleNames,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return valuesToSearch.includes(lowercased);
    });

    setFilteredCompanies(filtered);
  }, [searchText, companies]);

  const [widgets, setWidgets] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    newClients: 0,
  });

  useEffect(() => {
    const fetchWidgets = async () => {
      try {
        const res = await getClientWidgets(empToken);
        setWidgets({
          total: res.counts.clients.total,
          active: res.counts.active_client_status.active_client,
          inactive: res.counts.inactive_client_status.inactive_client,
          newClients: res.counts.new_client.total_new_client,
        });
      } catch (err) {
        console.error("Failed to load widget stats", err);
      }
    };

    fetchWidgets();
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      if (!canRead) return;
      try {
        const data = await fetchClients(empToken);
        setCompanies(
          data.map((c, i) => ({
            ...c,
            __tableKey: i,
            modules: Array.isArray(c.modules)
              ? c.modules.map((mod: any) =>
                  typeof mod === "string"
                    ? { model_id: mod, name: `Module ${mod}` }
                    : mod
                )
              : [],
          }))
        );
      } catch (error) {
        console.error("Failed to fetch companies", error);
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [canRead]);

  const refreshData = async () => {
    setLoading(true);
    try {
      const [clients, widgetData] = await Promise.all([
        fetchClients(empToken),
        getClientWidgets(empToken),
      ]);

      setCompanies(
        clients.map((c, i) => ({
          ...c,
          __tableKey: i,
          modules: Array.isArray(c.modules)
            ? c.modules.map((mod: any) =>
                typeof mod === "object"
                  ? mod
                  : {
                      model_id: mod,
                      name: `Module ${mod}`,
                    }
              )
            : [],
        }))
      );

      setWidgets({
        total: widgetData.counts.clients.total,
        active: widgetData.counts.active_client_status.active_client,
        inactive: widgetData.counts.inactive_client_status.inactive_client,
        newClients: widgetData.counts.new_client.total_new_client,
      });
    } catch (err) {
      console.error("Failed to refresh data", err);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.company_name.trim() !== "" &&
      formData.company_license.trim() !== "" &&
      formData.product_id.trim() !== "" &&
      formData.modules.length > 0 // since it's required in your UI
    );
  };

  const columns = [
    {
      title: "S.No",
      key: "serial",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
      sorter: (a: FetchedClient, b: FetchedClient) =>
        a.company_name.length - b.company_name.length,
      render: (_text: string, record: FetchedClient) => {
        const batchIconMap: Record<string, string> = {
          gold: "🥇",
          silver: "🥈",
          diamond: "💠",
          platinum: "🏆",
        };

        const batch = record.company_batch;
        const batchIcon = batchIconMap[batch?.toLowerCase()] || null;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontWeight: 600, color: "#222" }}>
              {record.company_name?.toUpperCase() || "-"}
            </span>

            {batch && (
              <span
                style={{
                  fontSize: 12,
                  opacity: 0.7,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  textTransform: "capitalize",
                }}
              >
                {batchIcon && <span>{batchIcon}</span>}
                <span>{batch}</span>
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Company Licence",
      dataIndex: "company_license",
      render: (text: string, record: FetchedClient) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="ms-2">
            <h6 className="fw-medium">
              <Link to="#">{record.company_license}</Link>
            </h6>
          </div>
        </div>
      ),
      sorter: (a: FetchedClient, b: FetchedClient) =>
        a.company_license.length - b.company_license.length,
    },
    {
      title: "Server URL",
      dataIndex: "server_name",
      sorter: (a: FetchedClient, b: FetchedClient) =>
        a.server_name.length - b.server_name.length,
      render: (text: string) => capitalizeFirstLetter(text),
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a: FetchedClient, b: FetchedClient) =>
        a.email.length - b.email.length,
    },
    {
      title: "Products",
      dataIndex: undefined,
      render: (_: any, record: FetchedClient) => (
        <div className="d-flex align-items-center justify-content-between">
          <p className="mb-0 me-2">{record.product?.product_name || "N/A"}</p>
        </div>
      ),
      sorter: (a: FetchedClient, b: FetchedClient) => {
        const nameA = a.product?.product_name || "";
        const nameB = b.product?.product_name || "";
        return nameA.length - nameB.length;
      },
    },
    {
      title: "Modules",
      dataIndex: undefined,
      render: (_: any, record: FetchedClient) => {
        const modules = record.modules;

        const displayModules = Array.isArray(modules)
          ? modules.map((mod: any) =>
              typeof mod === "object"
                ? { key: mod.model_id?.toString(), label: mod.name }
                : { key: mod.toString(), label: mod.toString() }
            )
          : [];

        return (
          <Dropdown>
            <Dropdown.Toggle as={Button}>Modules</Dropdown.Toggle>
            <Dropdown.Menu>
              {displayModules.map((mod) => (
                <Dropdown.Item key={mod.key}>{mod.label}</Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        );
      },
      sorter: (a: FetchedClient, b: FetchedClient) =>
        a.modules.length - b.modules.length,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string, record: FetchedClient) => {
        const status = record.status.toLowerCase();
        const statusText = status.charAt(0).toUpperCase() + status.slice(1);

        return (
          <div className="d-flex align-items-center justify-content-between">
            <p
              className={`mb-0 me-2 ${
                status === "active" ? "text-success" : "text-danger"
              }`}
            >
              {statusText}
            </p>
          </div>
        );
      },
      sorter: (a: FetchedClient, b: FetchedClient) =>
        a.status.length - b.status.length,
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_: any, record: FetchedClient) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            className="me-2"
            onClick={() => {
              if (!canUpdate) {
                toast.warning("You don't have permission to update companies.");
                return;
              }
              setEditClientId(record.client_id);

              setEditFormData({
                company_name: record.company_name || "",
                company_license: record.company_license || "",
                email: record.email || "",
                server_name: record.server_name || "",
                contact_number: record.contact_number?.toString() || "",
                company_batch: record.company_batch || "",
                status: record.status || "Active",
                product_id: record.product?.product_id?.toString() || "",
                modules: Array.isArray(record.modules)
                  ? record.modules
                      .map((mod: any) =>
                        typeof mod === "object"
                          ? mod.model_id?.toString()
                          : mod?.toString()
                      )
                      .filter(Boolean)
                  : [],
              });

              if (record.product) {
                const selectedProduct = products.find(
                  (p) => p.product_id === record.product?.product_id
                );
                if (selectedProduct && selectedProduct.modules) {
                  setAvailableModules(
                    selectedProduct.modules.map((m: any) => ({
                      module_id: m.module_id,
                      module_name: m.module_name.trim(),
                    }))
                  );
                }
              } else {
                setAvailableModules([]);
              }
            }}
            data-bs-toggle={canUpdate ? "modal" : undefined}
            data-bs-target={canUpdate ? "#edit_company" : undefined}
          >
            <i className="ti ti-edit" />
          </Link>
          <button
            className="btn btn-link p-0 text-danger"
            onClick={() => handleDelete(record.client_id)}
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];

  interface Product {
    product_id: number | undefined;
    product_name: string;
    description?: string;
    modules?: { module_name: string }[];
  }

  const initialFormData = {
    company_name: "",
    company_license: "",
    email: "",
    server_name: "",
    contact_number: "",
    product_id: "",
    modules: [] as string[],
    company_batch: "",
    status: "Active",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [products, setProducts] = useState<Product[]>([]);
  const [availableModules, setAvailableModules] = useState<
    { module_id: number; module_name: string }[]
  >([]);

  const loadProducts = async () => {
    try {
      const result = await fetchProducts(empToken);
      setProducts(
        result.map((product) => ({
          ...product,
          product_id: product.product_id ?? 0,
        }))
      );
    } catch (error) {
      console.error("Failed to load products", error);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const ALLOWED_EMAIL_DOMAINS = new Set([
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "iattechnologies.com",
    "deliveryotter.com",
    "royaltechsystems.co.in",
  ]);

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "company_name":
        if (!value.trim()) return "Company name is required";
        if (/\d/.test(value)) return "Company name must not contain numbers";
        break;
      case "company_license":
        if (!value.trim()) return "Company license is required";
        if (value.length > 10)
          return "Company license must be at less than or equal 10 characters";
        break;
    }
    return "";
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { id, value, multiple, selectedOptions } =
      e.target as HTMLSelectElement;

    if (id === "modules" && multiple) {
      const values = Array.from(selectedOptions, (option) => option.value);
      setFormData((prev) => ({ ...prev, modules: values }));
    } else {
      const newValue = id === "company_name" ? value.toUpperCase() : value;
      setFormData((prev) => ({ ...prev, [id]: newValue }));
      if (touched[id]) {
        const error = validateField(id, value);
        setErrors((prev) => ({ ...prev, [id]: error }));
      }

      if (id === "product_id") {
        const selectedProduct = products.find(
          (p) => p.product_id === Number(value)
        );
        if (selectedProduct && selectedProduct.modules) {
          setAvailableModules(
            selectedProduct.modules.map((m: any) => ({
              module_id: m.module_id,
              module_name: m.module_name.trim(),
            }))
          );
          setFormData((prev) => ({ ...prev, modules: [] }));
        } else {
          setAvailableModules([]);
          setFormData((prev) => ({ ...prev, modules: [] }));
        }
      }
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { id, value } = e.target;
    const error = validateField(id, value);
    setErrors((prev) => ({ ...prev, [id]: error }));
    setTouched((prev) => ({ ...prev, [id]: true }));
  };

  const handleSave = async () => {
    if (!canCreate) {
      toast.warning("You don't have permission to create companies.");
      return;
    }

    if (
      !formData.company_name ||
      !formData.product_id ||
      !formData.company_license
    ) {
      toast.warning("Please fill all required fields.");
      return;
    }

    const selectedProduct = products.find(
      (p) => p.product_id === Number(formData.product_id)
    );

    if (!selectedProduct || selectedProduct.product_id === undefined) {
      toast.error("Selected product not found or invalid!");
      return;
    }

    const payload = {
      client_id: "",
      company_name: formData.company_name,
      company_license: formData.company_license,
      email: formData.email?.trim() || "",
      server_name: formData.server_name,
      contact_number: Number(formData.contact_number),
      company_batch: formData.company_batch,
      status: formData.status,
      modules: formData.modules,
      product_id: selectedProduct.product_id,
      product: {
        product_id: selectedProduct.product_id,
        product_name: selectedProduct.product_name,
      },
    };

    try {
      await createClient(payload, empToken);
      setFormData(initialFormData);
      await refreshData();
    } catch (error) {
      console.error("Error creating client:", error);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({});
    setTouched({});
  };

  const [productList, setProductList] = useState<ProductType[]>([]);
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const result = await fetchProductList(empToken);
        setProductList(result);
      } catch (error) {
        console.error("Failed to load products", error);
      }
    };
    loadProducts();
  }, []);

  const [editFormData, setEditFormData] = useState({ ...initialFormData });
  const [editClientId, setEditClientId] = useState<string | null>(null);

  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { id, value, selectedOptions, multiple } =
      e.target as HTMLSelectElement;

    if (id === "modules" && multiple) {
      const values = Array.from(selectedOptions, (option) => option.value);
      setEditFormData((prev) => ({ ...prev, modules: values }));
    } else {
      setEditFormData((prev) => ({ ...prev, [id]: value }));

      if (id === "product_id") {
        const selectedProduct = products.find(
          (p) => p.product_id === Number(value)
        );
        if (selectedProduct && selectedProduct.modules) {
          setAvailableModules(
            selectedProduct.modules.map((m: any) => ({
              module_id: m.module_id,
              module_name: m.module_name.trim(),
            }))
          );
          setEditFormData((prev) => ({ ...prev, modules: [] }));
        } else {
          setAvailableModules([]);
          setEditFormData((prev) => ({ ...prev, modules: [] }));
        }
      }
    }
  };

  const handleUpdate = async () => {
    if (!canUpdate) {
      toast.warning("You don't have permission to update companies.");
      return;
    }

    if (
      !editFormData.company_name ||
      !editFormData.product_id ||
      !editFormData.company_license
    ) {
      toast.warning("Please fill all required fields.");
      return;
    }
    if (!editClientId) return;

    const selectedProduct = products.find(
      (p) => p.product_id === Number(editFormData.product_id)
    );

    if (!selectedProduct || selectedProduct.product_id === undefined) {
      toast.error("Product not found or invalid!");
      return;
    }

    const payload: Client = {
      client_id: editClientId,
      company_name: editFormData.company_name,
      company_license: editFormData.company_license,
      email: editFormData.email,
      server_name: editFormData.server_name,
      contact_number: Number(editFormData.contact_number),
      company_batch: editFormData.company_batch,
      status: editFormData.status,
      modules: editFormData.modules,
      product_id: selectedProduct.product_id,
      product: {
        product_id: selectedProduct.product_id,
        product_name: selectedProduct.product_name,
        description: selectedProduct.description ?? "",
      },
    };

    try {
      await updateClient(editClientId, payload, empToken);
      await refreshData();
      resetEditForm();
    } catch (err) {
      console.error(err);
    }
  };

  const resetEditForm = () => {
    setEditFormData({ ...initialFormData });
    setEditClientId(null);
    setAvailableModules([]);
  };

  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);

  const handleSelectionChange = (
    selectedRowKeys: any[],
    selectedRows: any[]
  ) => {
    const selectedClientIDs = selectedRows.map((row) => row.client_id);
    setSelectedClientIds(selectedClientIDs);
  };

  const handleAddCompanyClick = () => {
    if (!canCreate) {
      toast.warning("You don't have permission to create companies.");
      return;
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Companies</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">       
                    Companies
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <ClientExportImport
                selectedClientIds={selectedClientIds}
                onRefreshData={refreshData}
                canExport={canExport}       
              />
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-primary px-4 py-2 rounded d-flex align-items-center"
                  onClick={handleAddCompanyClick}
                  data-bs-toggle={canCreate ? "modal" : undefined}
                  data-bs-target={canCreate ? "#con-close-modal" : undefined}
                >
                  <span className="w-full text-center block">Add Company</span>
                </button>
              </div>
              <div className="ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center overflow-hidden">
                    <span className="avatar avatar-lg bg-primary flex-shrink-0">
                      <i className="ti ti-building fs-16" />
                    </span>
                    <div className="ms-2 overflow-hidden">
                      <p className="fs-12 fw-medium mb-1 text-truncate">
                        Total Companies
                      </p>
                      <h4>{widgets.total}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center overflow-hidden">
                    <span className="avatar avatar-lg bg-success flex-shrink-0">
                      <i className="ti ti-building fs-16" />
                    </span>
                    <div className="ms-2 overflow-hidden">
                      <p className="fs-12 fw-medium mb-1 text-truncate">
                        Active Companies
                      </p>
                      <h4>{widgets.active}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center overflow-hidden">
                    <span className="avatar avatar-lg bg-danger flex-shrink-0">
                      <i className="ti ti-building fs-16" />
                    </span>
                    <div className="ms-2 overflow-hidden">
                      <p className="fs-12 fw-medium mb-1 text-truncate">
                        Inactive Companies
                      </p>
                      <h4>{widgets.inactive}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center overflow-hidden">
                    <span className="avatar avatar-lg bg-skyblue flex-shrink-0">
                      <i className="ti ti-user-plus fs-16" />
                    </span>
                    <div className="ms-2 overflow-hidden">
                      <p className="fs-12 fw-medium mb-1 text-truncate">
                        New Clients
                      </p>
                      <h4>{widgets.newClients}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Companies</h5>
              <Input.Search
                placeholder="Search name..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ maxWidth: 300 }}
              />
            </div>
            <div className="card-body p-0">
              <Table
                dataSource={searchText ? filteredCompanies : companies}
                columns={columns}
                rowKey="client_id"
                Selection={true}
                loading={loading}
                onSelectionChange={handleSelectionChange}
              />
            </div>
          </div>
          <div
            id="con-close-modal"
            className="modal fade"
            tabIndex={-1}
            role="dialog"
            aria-hidden="true"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Add Company</h4>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    data-bs-toggle="tooltip"
                    data-bs-placement="left"
                    title="Close"
                  />
                </div>
                <div className="modal-body p-4">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="company_name" className="form-label">
                          Company Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            touched.company_name && errors.company_name
                              ? "is-invalid"
                              : ""
                          }`}
                          id="company_name"
                          placeholder="Enter company name"
                          value={formData.company_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.company_name && errors.company_name && (
                          <div className="invalid-feedback">
                            {errors.company_name}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="company_license" className="form-label">
                          Company Licence <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            touched.company_license && errors.company_license
                              ? "is-invalid"
                              : ""
                          }`}
                          id="company_license"
                          placeholder="Enter company licence"
                          value={formData.company_license}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.company_license && errors.company_license && (
                          <div className="invalid-feedback">
                            {errors.company_license}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">
                          E-mail
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="email"
                          placeholder="Enter email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="server_name" className="form-label">
                          Server Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="server_name"
                          placeholder="Server Name"
                          value={formData.server_name}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="contact_number" className="form-label">
                          Contact No.
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="contact_number"
                          placeholder="Contact Number"
                          value={formData.contact_number}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="product_id" className="form-label">
                          Product Name <span className="text-danger">*</span>
                        </label>
                        <Select
                          id="select_product_id"
                          options={productList.map((prod) => ({
                            value: prod.product_id.toString(),
                            label: prod.product_name,
                          }))}
                          value={
                            productList
                              .map((prod) => ({
                                value: prod.product_id.toString(),
                                label: prod.product_name,
                              }))
                              .find(
                                (opt) => opt.value === formData.product_id
                              ) || null
                          }
                          onChange={(selectedOption: OptionType | null) => {
                            const chosenProductId = selectedOption
                              ? selectedOption.value
                              : "";
                            setFormData((prev) => ({
                              ...prev,
                              product_id: chosenProductId,
                              modules: [],
                            }));
                            const chosenProduct = productList.find(
                              (p) => p.product_id.toString() === chosenProductId
                            );
                            if (chosenProduct) {
                              setAvailableModules(
                                chosenProduct.modules.map((mod) => ({
                                  module_id: mod.module_id,
                                  module_name: mod.module_name.trim(),
                                }))
                              );
                            } else {
                              setAvailableModules([]);
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="modules" className="form-label">
                          Module Name <span className="text-danger">*</span>
                        </label>
                        <Select
                          isMulti
                          id="select_modules"
                          options={availableModules.map((mod) => ({
                            value: mod.module_id.toString(),
                            label: mod.module_name,
                          }))}
                          value={formData.modules.map((id) => {
                            const selectedMod = availableModules.find(
                              (mod) => mod.module_id.toString() === id
                            );
                            return selectedMod
                              ? {
                                  value: selectedMod.module_id.toString(),
                                  label: selectedMod.module_name,
                                }
                              : { value: id, label: id };
                          })}
                          onChange={(selectedOptions: OptionType[]) => {
                            setFormData((prev) => ({
                              ...prev,
                              modules: selectedOptions.map((opt) => opt.value),
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="company_batch" className="form-label">
                          Company Batch
                        </label>
                        <select
                          className="form-select"
                          id="company_batch"
                          name="company_batch"
                          value={formData.company_batch}
                          onChange={handleChange}
                        >
                          <option value="">Select Batch</option>
                          <option value="Gold">Gold</option>
                          <option value="Silver">Silver</option>
                          <option value="Diamond">Diamond</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label">
                          Status
                        </label>
                        <select
                          id="status"
                          className="form-control"
                          value={formData.status}
                          onChange={handleChange}
                        >
                          <option value="">Select Status</option>
                          <option value="Active">Active</option>
                          <option value="Inactive">In Active</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-cancel me-2"
                    onClick={resetForm}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary waves-effect waves-light"
                    data-bs-dismiss="modal"
                    onClick={handleSave}
                    disabled={!canCreate || !isFormValid()}
                  >
                    Save changes
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-6">
            <div
              id="edit_company"
              className="modal fade"
              tabIndex={-1}
              role="dialog"
              aria-hidden="true"
            >
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h4 className="modal-title">Edit Company</h4>
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                      data-bs-toggle="tooltip"
                      data-bs-placement="left"
                      title="Close"
                    />
                  </div>
                  <div className="modal-body p-4">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="company_name" className="form-label">
                            Company Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="company_name"
                            value={editFormData.company_name}
                            onChange={handleEditChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label
                            htmlFor="company_license"
                            className="form-label"
                          >
                            Company License{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="company_license"
                            value={editFormData.company_license}
                            onChange={handleEditChange}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label htmlFor="email" className="form-label">
                            E-mail
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            value={editFormData.email}
                            onChange={handleEditChange}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="server_name" className="form-label">
                            Server Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="server_name"
                            value={editFormData.server_name}
                            onChange={handleEditChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label
                            htmlFor="contact_number"
                            className="form-label"
                          >
                            Contact No.
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="contact_number"
                            value={editFormData.contact_number}
                            onChange={handleEditChange}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="product_id" className="form-label">
                            Product Name <span className="text-danger">*</span>
                          </label>
                          <Select
                            id="edit_product_id"
                            options={productList.map((prod) => ({
                              value: prod.product_id.toString(),
                              label: prod.product_name,
                            }))}
                            value={
                              productList
                                .map((prod) => ({
                                  value: prod.product_id.toString(),
                                  label: prod.product_name,
                                }))
                                .find(
                                  (opt) => opt.value === editFormData.product_id
                                ) || null
                            }
                            onChange={(selectedOption: OptionType | null) => {
                              const chosenProductId = selectedOption
                                ? selectedOption.value
                                : "";
                              setEditFormData((prev) => ({
                                ...prev,
                                product_id: chosenProductId,
                                modules: [],
                              }));
                              const chosenProduct = productList.find(
                                (p) =>
                                  p.product_id.toString() === chosenProductId
                              );
                              if (chosenProduct) {
                                setAvailableModules(
                                  chosenProduct.modules.map((mod) => ({
                                    module_id: mod.module_id,
                                    module_name: mod.module_name.trim(),
                                  }))
                                );
                              } else {
                                setAvailableModules([]);
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="modules" className="form-label">
                            Module Name <span className="text-danger">*</span>
                          </label>
                          <Select
                            isMulti
                            id="edit_modules"
                            options={availableModules.map((mod) => ({
                              value: mod.module_id.toString(),
                              label: mod.module_name,
                            }))}
                            value={editFormData.modules.map((id) => {
                              const selectedMod = availableModules.find(
                                (mod) => mod.module_id.toString() === id
                              );
                              return selectedMod
                                ? {
                                    value: selectedMod.module_id.toString(),
                                    label: selectedMod.module_name,
                                  }
                                : { value: id, label: id };
                            })}
                            onChange={(selectedOptions: OptionType[]) => {
                              setEditFormData((prev) => ({
                                ...prev,
                                modules: selectedOptions.map(
                                  (opt) => opt.value
                                ),
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="company_batch" className="form-label">
                            Company Batch
                          </label>
                          <select
                            className="form-select"
                            id="company_batch"
                            name="company_batch"
                            value={editFormData.company_batch}
                            onChange={handleEditChange}
                          >
                            <option value="">Select Batch</option>
                            <option value="Gold">Gold</option>
                            <option value="Silver">Silver</option>
                            <option value="Diamond">Diamond</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="status" className="form-label">
                            Status
                          </label>
                          <select
                            id="status"
                            className="form-control"
                            value={editFormData.status}
                            onChange={handleEditChange}
                          >
                            <option value="">Select Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-cancel me-2"
                      onClick={resetEditForm}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary waves-effect waves-light"
                      data-bs-dismiss="modal"
                      onClick={handleUpdate}
                      disabled={!canUpdate}
                    >
                      Update Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Modal
            title="Confirm Deletion"
            open={showConfirm}
            onOk={confirmDelete}
            onCancel={() => setShowConfirm(false)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <p>Are you sure you want to delete this client?</p>
          </Modal>
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default TmsCompanies;
